import { logger } from '../../utils/logger.js'
import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import { getNotifications } from '../../services/teams/teams-api.js'
import '../location-search/location-search.js'

import '@material/web/menu/menu.js'
import '@material/web/menu/menu-item.js'

const log = logger.create('nav')

interface NavigationItem {
  id: string
  label: string
  icon: string
  path: string
}

@customElement('cairn-navigation')
export class CairnNavigation extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      background: var(--cairn-surface-color, #ffffff);
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      /* Extend background into safe area at bottom */
      padding-bottom: env(safe-area-inset-bottom);
    }

    .navigation {
      display: flex;
      height: 60px;
      width: 100%;
    }

    .nav-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border: none;
      background: none;
      padding: var(--cairn-spacing-xs, 4px);
      text-decoration: none;
      color: var(--cairn-text-secondary-color, #64748b);
      transition: all var(--cairn-animation-duration, 0.2s);
      min-width: 0;
    }

    .nav-item:hover {
      color: var(--cairn-primary-color, #3b82f6);
      background: rgba(59, 130, 246, 0.04);
    }

    .nav-item.active {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .nav-icon {
      margin-bottom: 2px;
      line-height: 1;
    }

    .nav-icon .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .nav-label {
      font-size: 11px;
      font-weight: 500;
      line-height: 1;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 100%;
    }

    /* Profile avatar in nav */
    .nav-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
    }

    .nav-icon-wrapper {
      position: relative;
      display: inline-flex;
    }

    .notification-badge {
      position: absolute;
      top: -4px;
      right: -6px;
      background: #dc2626;
      color: white;
      font-size: 10px;
      font-weight: 600;
      min-width: 16px;
      height: 16px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 4px;
      line-height: 1;
    }

    /* Profile menu anchor — must match .nav-item flex participation */
    .profile-anchor {
      position: relative;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
    }

    .profile-anchor .nav-item {
      flex: none;
      width: 100%;
      height: 100%;
    }

    /* Desktop sidebar layout */
    @media (min-width: 769px) {
      :host {
        border-top: none;
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        position: relative;
      }

      .navigation {
        flex-direction: column;
        height: 100vh;
        padding: var(--cairn-spacing-md, 16px) 0;
      }

      .nav-item {
        flex: none;
        height: 64px;
        padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
        flex-direction: column;
        justify-content: center;
        gap: var(--cairn-spacing-xs, 4px);
      }

      /* Hide profile and search items on desktop */
      .profile-anchor {
        display: none;
      }

      .nav-item.profile-item {
        display: none;
      }

      .nav-item.search-item {
        display: none;
      }

      .nav-icon {
        margin-bottom: 2px;
      }

      .nav-label {
        font-size: 12px;
        font-weight: 500;
      }
    }

    /* Mobile search popup - positioned where app status bar would be (above bottom nav) */
    .mobile-search-popup {
      position: fixed;
      bottom: calc(60px + env(safe-area-inset-bottom)); /* Above nav + safe area */
      left: 0;
      right: 0;
      height: 60px; /* Same height as status bar */
      background: var(--cairn-surface-color, #ffffff);
      border-top: 1px solid var(--cairn-border-color, #e0e0e0);
      box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.1);
      z-index: 1001; /* Above status bar */
      transform: translateY(100%);
      transition: transform 0.3s ease;
      padding: 8px 16px;
      display: none; /* Hidden by default */
      align-items: stretch; /* Make search input fill height */
    }

    .mobile-search-popup.open {
      display: flex;
      transform: translateY(0);
    }

    /* Ensure location search fills full width in popup */
    .mobile-search-popup location-search {
      flex: 1;
    }

    @media (min-width: 769px) {
      /* Hide mobile search on desktop */
      .mobile-search-popup {
        display: none;
      }
    }
  `

  @property({ type: String })
  currentView = 'map'

  @state()
  private showMobileSearch = false

  @state()
  private userLocation: [number, number] | null = null

  @state()
  private authState: AuthState = authService.getState()

  @state()
  private showProfileMenu = false

  @state()
  private unreadNotifications = 0

  private unsubAuth?: () => void
  private notificationPollInterval?: ReturnType<typeof setInterval>

  private navigationItems: NavigationItem[] = [
    { id: 'map', label: 'Map', icon: 'map', path: '/map' },
    { id: 'trips', label: 'Trips', icon: 'hiking', path: '/activities' },
    { id: 'search', label: 'Search', icon: 'search', path: '/map' }, // Mobile search button
    { id: 'teams', label: 'Teams', icon: 'groups', path: '/teams' },
    { id: 'settings', label: 'Profile', icon: 'person', path: '/settings' },
  ]

  private boundPollNotifications = (): void => {
    void this.pollNotifications()
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      if (state.status === 'authenticated') void this.pollNotifications()
    })
    if (this.authState.status === 'authenticated') void this.pollNotifications()
    this.notificationPollInterval = setInterval(() => void this.pollNotifications(), 60_000)
    window.addEventListener('notifications-changed', this.boundPollNotifications)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    if (this.notificationPollInterval) clearInterval(this.notificationPollInterval)
    window.removeEventListener('notifications-changed', this.boundPollNotifications)
  }

  private async pollNotifications(): Promise<void> {
    if (this.authState.status !== 'authenticated') return
    try {
      const notifications = await getNotifications()
      this.unreadNotifications = notifications.filter(n => !n.read).length
    } catch {
      // Silently ignore — notification polling is best-effort
    }
  }

  private handleNavigation(item: NavigationItem): void {
    if (item.id === 'search') {
      // Toggle mobile search popup AND navigate to map
      this.toggleMobileSearch()
      // Also dispatch navigation to map page
      this.dispatchEvent(
        new CustomEvent('navigate', {
          detail: { view: 'map', path: '/map' },
          bubbles: true,
          composed: true,
        })
      )
      return
    }

    if (item.id === 'settings') {
      // Toggle profile popup menu
      this.showProfileMenu = !this.showProfileMenu
      return
    }

    // Dispatch custom event for parent to handle navigation
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: item.id, path: item.path },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleProfileMenuNavigate(path: string): void {
    this.showProfileMenu = false
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'settings', path },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleLogin(): void {
    this.showProfileMenu = false
    void authService.login()
  }

  private handleLogout(): void {
    this.showProfileMenu = false
    void authService.logout()
  }

  private handleProfileMenuClose(): void {
    this.showProfileMenu = false
  }

  private handleHelpClick(): void {
    this.showProfileMenu = false
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'help', path: '/help' },
        bubbles: true,
        composed: true,
      })
    )
  }

  private toggleMobileSearch(): void {
    this.showMobileSearch = !this.showMobileSearch

    // Get current location when search is opened
    if (this.showMobileSearch && !this.userLocation) {
      void this.getCurrentLocation()
    }
  }

  private async getCurrentLocation(): Promise<void> {
    try {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          position => {
            this.userLocation = [position.coords.latitude, position.coords.longitude]
          },
          error => {
            log.warn('Could not get user location for search bias:', error.message)
          },
          {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 60000, // Use location cached within 1 minute
          }
        )
      }
    } catch (error) {
      log.warn('Geolocation not available for search bias')
    }
  }

  private handleLocationSelect(event: CustomEvent): void {
    // Forward location selection to parent components
    this.dispatchEvent(
      new CustomEvent('location-search-navigation', {
        detail: event.detail,
        bubbles: true,
        composed: true,
      })
    )

    // Close search popup after selection
    this.showMobileSearch = false
  }

  private renderNavIcon(item: NavigationItem): unknown {
    if (item.id === 'settings' && this.authState.picture) {
      return html`<img class="nav-avatar" src="${this.authState.picture}" alt="" />`
    }
    const icon = item.id === 'search' && this.showMobileSearch ? 'close' : item.icon
    if (item.id === 'teams' && this.unreadNotifications > 0) {
      return html`
        <span class="nav-icon-wrapper">
          <span class="material-icons">${icon}</span>
          <span class="notification-badge"
            >${this.unreadNotifications > 9 ? '9+' : this.unreadNotifications}</span
          >
        </span>
      `
    }
    return html`<span class="material-icons">${icon}</span>`
  }

  override render(): unknown {
    const isAuthenticated = this.authState.status === 'authenticated'

    return html`
      <nav class="navigation">
        ${this.navigationItems.map(
          item => html`
            ${item.id === 'settings'
              ? html`
                  <div class="profile-anchor" id="profile-anchor">
                    <button
                      class="nav-item ${this.currentView === 'settings'
                        ? 'active'
                        : ''} profile-item"
                      @click=${() => this.handleNavigation(item)}
                      aria-label="${item.label}"
                    >
                      <div class="nav-icon">${this.renderNavIcon(item)}</div>
                      <div class="nav-label">${item.label}</div>
                    </button>
                    <md-menu
                      anchor="profile-anchor"
                      positioning="popover"
                      anchor-corner="start-end"
                      menu-corner="end-end"
                      .open=${this.showProfileMenu}
                      @closed=${this.handleProfileMenuClose}
                    >
                      <md-menu-item @click=${() => this.handleProfileMenuNavigate('/settings')}>
                        <span
                          class="material-icons"
                          slot="start"
                          style="font-family: 'Material Symbols Outlined';"
                          >person</span
                        >
                        <span slot="headline">Profile</span>
                      </md-menu-item>
                      <md-menu-item @click=${this.handleHelpClick}>
                        <span
                          class="material-icons"
                          slot="start"
                          style="font-family: 'Material Symbols Outlined';"
                          >help_outline</span
                        >
                        <span slot="headline">Help</span>
                      </md-menu-item>
                      ${isAuthenticated
                        ? html`
                            <md-menu-item @click=${this.handleLogout}>
                              <span
                                class="material-icons"
                                slot="start"
                                style="font-family: 'Material Symbols Outlined';"
                                >logout</span
                              >
                              <span slot="headline">Sign out</span>
                            </md-menu-item>
                          `
                        : html`
                            <md-menu-item @click=${this.handleLogin}>
                              <span
                                class="material-icons"
                                slot="start"
                                style="font-family: 'Material Symbols Outlined';"
                                >login</span
                              >
                              <span slot="headline">Sign in</span>
                            </md-menu-item>
                          `}
                    </md-menu>
                  </div>
                `
              : html`
                  <button
                    class="nav-item ${this.currentView === item.id ? 'active' : ''} ${item.id ===
                    'search'
                      ? 'search-item'
                      : ''} ${item.id === 'search' && this.showMobileSearch ? 'active' : ''}"
                    @click=${() => this.handleNavigation(item)}
                    aria-label="${item.label}"
                  >
                    <div class="nav-icon">${this.renderNavIcon(item)}</div>
                    <div class="nav-label">${item.label}</div>
                  </button>
                `}
          `
        )}
      </nav>

      <!-- Mobile Search Popup -->
      <div class="mobile-search-popup ${this.showMobileSearch ? 'open' : ''}">
        <location-search
          @location-selected=${this.handleLocationSelect}
          placeholder="Search for a location..."
          .biasPoint=${this.userLocation}
        ></location-search>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-navigation': CairnNavigation
  }
}
